/**
 * THE INK MARK. One drawing per species, on the chart, instead of an icon beside a label.
 *
 * -- WHY EVERY SPECIES COMES OUT OF THE SAME MACHINE --------------------------
 *
 * The design ledger's hardest rule on this project, learned by rejection on 2026-08-30:
 * NEVER SHIP TWO FIDELITY TIERS OF THE SAME CONTENT CLASS ON ONE SURFACE. Four species had
 * painted art and sixteen fell back to a flat mark, and the good ones proved what the page
 * was supposed to look like. There are twenty-four species, so hand-drawing four and
 * stubbing twenty would repeat that exactly.
 *
 * -- WHY IT IS A SOLID SILHOUETTE ---------------------------------------------
 *
 * MEASURED, by opening the reference rather than reading a summary of it: Sable's fishing
 * bar - the confirmed minimalist-indie capture at
 * design-library/captures/game-ui/minimalist-indie/sable/01.jpg - carries its fish as ONE
 * SOLID BLACK SHAPE with no interior, no outline and no label beside it. The first draft of
 * this file drew gill lines, a pectoral stroke, a lateral line and a per-species pattern in
 * diluted ink; printed as ASCII and looked at, every mark read as a blob. Interior detail at
 * the mark's size does not survive. The silhouette is the whole drawing, and the one paper
 * pixel punched back through for the eye is what makes it an animal rather than a shape.
 *
 * THAT PARAGRAPH SAID "28x14" UNTIL 2026-09-03 AND HAD BEEN WRONG SINCE THE CAUDAL FINS WERE
 * RE-AUTHORED. Measured off the running build, the twenty-four marks are 37-63 art px on the
 * long axis by 18-35 deep, 360-769 ink pixels. The re-authoring is recorded eighty lines
 * down - "they were authored at half this size first" - and the header was never brought
 * along, so the file carried a figure 1.7x under its own drawings and every argument built on
 * it inherited the error. The CONCLUSION survives the correction, because the generation
 * floor is about 100 px on the long axis (CONTRACT 1.2c) and 63 is still well under it. The
 * number did not.
 *
 * -- AND WHY THAT IS AN ARGUMENT ABOUT A SIZE, NOT ABOUT FISH -----------------
 *
 * The owner asked why the fish do not carry the graphics the rest of the game carries. The
 * answer is that NO FISH HAS EVER BEEN RENDERED: there was no fish batch and no fish under
 * src/render/baked, and the reasoning in this header was never the thing standing in the way.
 * It is correct about every surface a fish is currently drawn on - the strip is 64 art px
 * tall, a ledger row is 11, and the catch mark lies in the world's metric space at 7.80 px/m
 * where a 100 px fish would be a 13 m animal beside a 10 m boat - and it says nothing at all
 * about a surface that does not exist yet. chart.ts PLATE_BOX is that surface: 482 x 128 art
 * px of free paper in the open ledger, where all twenty-four clear the floor. The marks stay
 * exactly as they are. They were never the problem and they are still right where they sit.
 *
 * -- WHAT AUTHORSHIP MEANS HERE -----------------------------------------------
 *
 * EIGHT BODY FAMILIES, assigned by hand from what the animal is - a grouper is cod-shaped
 * and a wahoo is not, and no rule derives that. Each family is a nine-point half-height
 * profile down the body, an authored caudal fin drawn pixel by pixel, and where its dorsal
 * and anal fins sit. Within a family, a species varies in length, depth and dorsal height
 * from its own id, so the difference between two marks is always a difference of SILHOUETTE.
 *
 * Nothing here scales, resamples or blends. The profile is evaluated once and rounded into
 * whole pixels, exactly as a hand placing them would.
 */

import { SPECIES } from '../sim/index';
import { bitmap, put, type Bitmap } from './glyphs';

/** The two values a mark paints. Indices into chart.ts's palette. */
export const MARK_INK = 1;
export const MARK_PAPER = 3;

interface Family {
  readonly id: string;
  /** Half-height at u = 0, 1/8, ... 1 along the body, in whole pixels. */
  readonly profile: readonly number[];
  readonly len: number;
  /** The caudal fin, top row first. '#' is ink. */
  readonly tail: readonly string[];
  /** Which row of `tail` is the fish's midline. */
  readonly tailMid: number;
  /** Dorsal humps as [uStart, uEnd, height] standing on the body's own edge. */
  readonly dorsal: readonly (readonly [number, number, number])[];
  readonly anal: readonly (readonly [number, number, number])[];
  /** A bill or rostrum this many pixels ahead of the body. */
  readonly bill: number;
  /** Tuna-style finlets along the peduncle. */
  readonly finlets: boolean;
}

/**
 * THE CAUDAL FINS, drawn pixel by pixel. Each is listed top row first with the row that is
 * the fish's own midline named beside it, so a fin hangs off the body's last column rather
 * than floating near it.
 *
 * They were authored at half this size first and the marks read as ICONS in the strip at
 * 1920x1080 - a 25 px drawing doubled is a 50 px smudge. These numbers are the drawing at
 * the size it is actually looked at; nothing is scaled, the profile is simply larger.
 */
const FORKED = [
  '.....##',
  '....###',
  '..####.',
  '.####..',
  '####...',
  '###....',
  '####...',
  '.####..',
  '..####.',
  '....###',
  '.....##',
];
const LUNATE = [
  '......##',
  '.....###',
  '....###.',
  '...###..',
  '..###...',
  '.####...',
  '####....',
  '###.....',
  '####....',
  '.####...',
  '..###...',
  '...###..',
  '....###.',
  '.....###',
  '......##',
];
const ROUNDED = ['...####', '..#####', '.######', '#######', '#######', '#######', '.######', '..#####', '...####'];
const HETEROCERCAL = [
  '.......##',
  '......###',
  '.....###.',
  '....###..',
  '...###...',
  '..####...',
  '.####....',
  '####.....',
  '####.....',
  '###......',
  '####.....',
  '.####....',
  '..###....',
  '...###...',
  '....##...',
];
const THREE_LOBED = [
  '....###..',
  '..#####..',
  '.######..',
  '########.',
  '#########',
  '########.',
  '.######..',
  '..#####..',
  '....###..',
];
const STUB = ['.##.', '####', '####', '####', '.##.'];

const FAMILIES: readonly Family[] = [
  {
    id: 'silver',
    profile: [0, 3, 6, 8, 8, 7, 5, 3, 2],
    len: 38,
    tail: FORKED,
    tailMid: 5,
    dorsal: [[0.34, 0.58, 5]],
    anal: [[0.6, 0.78, 3]],
    bill: 0,
    finlets: false,
  },
  {
    id: 'cod',
    profile: [0, 5, 8, 10, 9, 7, 5, 3, 2],
    len: 44,
    tail: ROUNDED,
    tailMid: 4,
    dorsal: [
      [0.2, 0.4, 6],
      [0.48, 0.64, 5],
      [0.72, 0.86, 3],
    ],
    anal: [
      [0.5, 0.66, 5],
      [0.74, 0.88, 3],
    ],
    bill: 0,
    finlets: false,
  },
  {
    id: 'torpedo',
    profile: [0, 3, 6, 8, 8, 7, 5, 3, 2],
    len: 48,
    tail: LUNATE,
    tailMid: 7,
    dorsal: [
      [0.26, 0.44, 8],
      [0.5, 0.62, 3],
    ],
    anal: [[0.52, 0.64, 3]],
    bill: 0,
    finlets: true,
  },
  {
    id: 'deep',
    profile: [0, 6, 10, 12, 12, 10, 7, 4, 2],
    len: 34,
    tail: ROUNDED,
    tailMid: 4,
    dorsal: [[0.2, 0.7, 5]],
    anal: [[0.3, 0.74, 5]],
    bill: 0,
    finlets: false,
  },
  {
    id: 'billed',
    profile: [0, 3, 6, 8, 7, 5, 4, 3, 2],
    len: 40,
    tail: LUNATE,
    tailMid: 7,
    dorsal: [
      [0.18, 0.46, 10],
      [0.56, 0.68, 3],
    ],
    anal: [[0.58, 0.7, 3]],
    bill: 15,
    finlets: false,
  },
  {
    id: 'ribbon',
    profile: [0, 5, 5, 5, 5, 5, 4, 3, 3],
    len: 56,
    tail: STUB,
    tailMid: 2,
    // The head crest first, then the ridge that runs the whole back. Looked at as a plate,
    // the ribbon without its crest was the one mark of the twenty-four that read as a shape
    // rather than an animal - a long ellipse with a stub on it.
    dorsal: [
      [0.0, 0.14, 9],
      [0.04, 0.96, 3],
    ],
    anal: [],
    bill: 0,
    finlets: false,
  },
  {
    id: 'shark',
    profile: [0, 3, 6, 8, 8, 7, 5, 4, 3],
    len: 48,
    tail: HETEROCERCAL,
    tailMid: 9,
    dorsal: [
      [0.3, 0.48, 8],
      [0.64, 0.76, 3],
    ],
    anal: [[0.66, 0.78, 3]],
    bill: 0,
    finlets: false,
  },
  {
    id: 'lobed',
    profile: [0, 5, 8, 10, 10, 8, 6, 4, 3],
    len: 40,
    tail: THREE_LOBED,
    tailMid: 4,
    dorsal: [
      [0.26, 0.4, 5],
      [0.6, 0.74, 5],
    ],
    anal: [[0.62, 0.76, 5]],
    bill: 0,
    finlets: false,
  },
];

const FAMILY_BY_ID = new Map(FAMILIES.map((f) => [f.id, f]));

/** WHICH ANIMAL IS WHICH SHAPE. By hand, from what the species is. */
const FAMILY_OF: Readonly<Record<string, string>> = {
  minnow: 'silver',
  sardine: 'silver',
  herring: 'silver',
  pilchard: 'silver',
  mackerel: 'torpedo',
  grey_mullet: 'cod',
  sea_bream: 'deep',
  pollock: 'cod',
  red_snapper: 'cod',
  sea_bass: 'cod',
  cod: 'cod',
  grouper: 'cod',
  amberjack: 'torpedo',
  wahoo: 'torpedo',
  yellowfin: 'torpedo',
  swordfish: 'billed',
  opah: 'deep',
  bluefin: 'torpedo',
  marlin: 'billed',
  oarfish: 'ribbon',
  sunfish: 'deep',
  coelacanth: 'lobed',
  lanternshark: 'shark',
  moonfish: 'deep',
};

/**
 * WHERE A SPECIES SITS INSIDE ITS OWN FAMILY, counting up the species table.
 *
 * The first draft varied length, depth and dorsal height from a HASH of the species id, and
 * chart.test.ts caught what that costs: pilchard and sardine hashed onto the same three
 * values and drew the identical silhouette. A hash gives no guarantee of distinctness, and
 * "no two species are the same drawing" is exactly the claim this file exists to make.
 *
 * An ordinal does guarantee it. Length cycles every 3 and depth every 3, so the first nine
 * members of a family are nine distinct (length, depth) pairs; the largest family this build
 * ships has six. Adding a twenty-fifth species widens the sweep with no edit here.
 */
const ORDINAL: ReadonlyMap<string, number> = (() => {
  const seen = new Map<string, number>();
  const out = new Map<string, number>();
  for (const s of SPECIES) {
    const fam = FAMILY_OF[s.id] ?? 'cod';
    const k = seen.get(fam) ?? 0;
    out.set(s.id, k);
    seen.set(fam, k + 1);
  }
  return out;
})();

export interface MarkSpec {
  readonly family: Family;
  readonly len: number;
  readonly depth: number;
  readonly dorsalAdj: number;
}

export function markSpecFor(speciesId: string): MarkSpec {
  const family = FAMILY_BY_ID.get(FAMILY_OF[speciesId] ?? 'cod') ?? FAMILIES[1]!;
  const k = ORDINAL.get(speciesId) ?? 0;
  const len = family.len + ([-4, 0, 4][k % 3] ?? 0);
  const peak = Math.max(...family.profile);
  const depth = peak + ([0, -2, 2][Math.floor(k / 3) % 3] ?? 0);
  return { family, len, depth, dorsalAdj: Math.floor(k / 9) % 2 };
}

/** Half-height at column i, from the family's nine control points. */
function halfAt(spec: MarkSpec, i: number): number {
  const { family, len, depth } = spec;
  const peak = Math.max(...family.profile);
  const u = len <= 1 ? 0 : i / (len - 1);
  const s = u * (family.profile.length - 1);
  const a = Math.min(family.profile.length - 1, Math.max(0, Math.floor(s)));
  const b = Math.min(family.profile.length - 1, a + 1);
  const f = s - a;
  const raw = (family.profile[a] ?? 0) * (1 - f) + (family.profile[b] ?? 0) * f;
  return Math.max(0, Math.round((raw * depth) / peak));
}

export interface Mark {
  readonly bmp: Bitmap;
  /** Row in `bmp` that is the fish's own midline, so the chart can sit it on a line. */
  readonly mid: number;
}

function finRise(u: number, a: number, b: number, h: number): number {
  if (u < a || u > b) return 0;
  const t = (u - a) / Math.max(1e-6, b - a);
  return Math.round(h * Math.sin(Math.PI * t));
}

export function fishMark(speciesId: string): Mark {
  const spec = markSpecFor(speciesId);
  const { family, len, dorsalAdj } = spec;
  const dorsal = family.dorsal.map(([a, b, h]) => [a, b, h + dorsalAdj] as const);
  const tailW = family.tail.reduce((m, r) => Math.max(m, r.length), 0);

  let maxUp = family.tailMid;
  let maxDown = family.tail.length - 1 - family.tailMid;
  for (let i = 0; i < len; i++) {
    const h = halfAt(spec, i);
    const u = len <= 1 ? 0 : i / (len - 1);
    let up = h;
    let down = h;
    for (const [a, b, hh] of dorsal) up = Math.max(up, h + finRise(u, a, b, hh));
    for (const [a, b, hh] of family.anal) down = Math.max(down, h + finRise(u, a, b, hh));
    if (family.finlets) {
      up = Math.max(up, h + 2);
      down = Math.max(down, h + 2);
    }
    maxUp = Math.max(maxUp, up);
    maxDown = Math.max(maxDown, down);
  }

  const w = family.bill + len + tailW;
  const h = maxUp + maxDown + 1;
  const mid = maxUp;
  const bmp = bitmap(w, h);
  const x0 = family.bill;

  for (let i = 0; i < family.bill; i++) put(bmp, i, mid, MARK_INK);

  for (let i = 0; i < len; i++) {
    const hh = halfAt(spec, i);
    const u = len <= 1 ? 0 : i / (len - 1);
    for (let dy = -hh; dy <= hh; dy++) put(bmp, x0 + i, mid + dy, MARK_INK);
    for (const [a, b, fh] of dorsal) {
      for (let k = 1; k <= finRise(u, a, b, fh); k++) put(bmp, x0 + i, mid - hh - k, MARK_INK);
    }
    for (const [a, b, fh] of family.anal) {
      for (let k = 1; k <= finRise(u, a, b, fh); k++) put(bmp, x0 + i, mid + hh + k, MARK_INK);
    }
    if (family.finlets && u > 0.68 && u < 0.96 && i % 5 === 0) {
      put(bmp, x0 + i, mid - hh - 1, MARK_INK);
      put(bmp, x0 + i, mid - hh - 2, MARK_INK);
      put(bmp, x0 + i, mid + hh + 1, MARK_INK);
      put(bmp, x0 + i, mid + hh + 2, MARK_INK);
    }
  }

  for (let ty = 0; ty < family.tail.length; ty++) {
    const row = family.tail[ty] ?? '';
    for (let tx = 0; tx < row.length; tx++) {
      if (row[tx] === '#') put(bmp, x0 + len - 1 + tx, mid - family.tailMid + ty, MARK_INK);
    }
  }

  // The eye: the paper punched back through the ink. Two pixels across at this size,
  // because one is lost in the doubling, and it is the whole difference between a shape and
  // an animal.
  const ex = x0 + Math.max(2, Math.round(len * 0.1));
  const ey = mid - (spec.depth >= 8 ? 2 : 1);
  for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) put(bmp, ex + dx, ey + dy, MARK_PAPER);

  return { bmp, mid };
}

/** Every mark this build can draw, built once. Twenty-four, all from the same machine. */
export const MARKS: ReadonlyMap<string, Mark> = new Map(SPECIES.map((s) => [s.id, fishMark(s.id)]));
